import { ReactElement, ElementType } from 'react';
import { Typography, Button, Link, Stack } from '@mui/material';
import { TabContext } from '@mui/lab';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon } from '@mui/icons-material';

import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';

import {
  DialogImportFromAssociatedCompanyContextPanelsId,
  useDialogImportFromAssociatedCompanyContext,
} from './DialogImportFromAssociatedCompanyContext';
import {
  DialogImportFromAssociatedCompanyNotAssociatedCompanies,
  DialogImportFromAssociatedCompanyImporter,
} from './Tabs';
import {
  DialogImportFromAssociatedCompanyHeader,
  DialogImportFromAssociatedCompanyHeaderCloseIcon,
  DialogImportFromAssociatedCompanyContentContainer,
  DialogImportFromAssociatedCompanyTabPanel,
  DialogImportFromAssociatedCompanyActions,
} from './styles';

interface DialogImportFromAssociatedCompanyTabPanelItem {
  id: DialogImportFromAssociatedCompanyContextPanelsId;
  panel: ElementType;
}

const TABS_PANELS: DialogImportFromAssociatedCompanyTabPanelItem[] = [
  {
    id: 'notAssociatedCompanies',
    panel: DialogImportFromAssociatedCompanyNotAssociatedCompanies,
  },
  {
    id: 'importer',
    panel: DialogImportFromAssociatedCompanyImporter,
  },
];

const DialogImportFromAssociatedCompanyContent = (): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();

  const {
    onClose,
    activeTab,
    selectedOrders: { selectedRowsIds: selectedOrdersIds },
    importRequest: { executeImport, loading: loadingImport },
  } = useDialogImportFromAssociatedCompanyContext();

  return (
    <>
      <DialogImportFromAssociatedCompanyHeader>
        <Typography component="h2" variant="h6">
          Recepcionar pedidos de dadores o vendedores
        </Typography>

        <DialogImportFromAssociatedCompanyHeaderCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogImportFromAssociatedCompanyHeaderCloseIcon>
      </DialogImportFromAssociatedCompanyHeader>

      <DialogImportFromAssociatedCompanyContentContainer dividers>
        <TabContext value={activeTab}>
          {TABS_PANELS.map(({ id, panel: Panel }) => (
            <DialogImportFromAssociatedCompanyTabPanel
              key={`dialog-import-orders-from-associated-company-panel-${id}`}
              value={id}
            >
              <Panel />
            </DialogImportFromAssociatedCompanyTabPanel>
          ))}
        </TabContext>
      </DialogImportFromAssociatedCompanyContentContainer>

      <DialogImportFromAssociatedCompanyActions>
        <Typography
          variant="caption"
          component="span"
          // @NEW-GIVER-DASHBOARD: Hide until the help URL is available for Givers
          visibility={isCarrier ? 'visible' : 'hidden'}
        >
          <Link
            href="https://routix.tawk.help/article/rutas-de-colecta-y-distribución-de-terceros"
            target="_blank"
            underline="hover"
            fontWeight="bold"
          >
            Haz clic aquí
          </Link>{' '}
          para tener ayuda sobre la gestión de pedidos de dadores de carga.
        </Typography>

        <Stack spacing={2} direction="row">
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {activeTab === 'importer' && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={executeImport}
              loading={loadingImport}
              disabled={selectedOrdersIds.length < 1}
            >
              Recepcionar pedidos
            </LoadingButton>
          )}
        </Stack>
      </DialogImportFromAssociatedCompanyActions>
    </>
  );
};

export default DialogImportFromAssociatedCompanyContent;
